import htmlDocx from 'html-docx-js/dist/html-docx';
import React, { useContext, useEffect, useState } from "react";
import {
	Button,
	Form,
	Modal
} from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { LoaderContext } from "../../../App";
import { ReportPageType, ReportSection } from "../../../core/constants/common";
import ReportService from "../../../core/service/ReportService";
import ReportingUtils from "../../../core/utilities/ReportingUtils";
import Utils from "../../../core/utilities/Utils";
import { prepareGhgModalCoverHtmlForWord } from "../../../core/utilities/ghgWordExportDesign";
import {
	prepareGhgTocPageHtmlForWord,
	prepareGhgWordExportDocument,
	stripGhgCoverPageBorderForWord,
} from "../../../core/utilities/ghgWordExportHtml";
import {
	buildExportModalTocHtml,
	buildGhgTableOfContentsHtml,
	escapeTocHtml,
} from "../../../core/utilities/reportTocWordHtml";
import ReportWordExportUtils from "../../../core/utilities/ReportWordExportUtils";
import {
	WORD_DOCX_GHG_COMPAT_INLINE_CSS,
	WORD_DOCX_GHG_COVER_BRSR_TABLE_BORDER_RESET_CSS,
	WORD_DOCX_GHG_COVER_NO_BORDER_CSS,
	WORD_DOCX_GHG_TABLE_P_FULL_WIDTH_CSS,
} from "../../../core/utilities/wordDocxGhgCompatStyles";
import { WORD_DOCX_GHG_INNER_IMAGE_CSS } from "../../../core/utilities/wordDocxExportPrep";

const ExportAs = {
	PDF: 'PDF',
	WORD: 'WORD'
};

const isGhgLikeExportView = (view) => view === 'GHG' || view === 'UAE_FDL';

function ReportExportModal({ t, reportId, exportAs, onHide, activeView }) {

	const { setLoaderDisplay } = useContext(LoaderContext);
	const [report, setReport] = useState('');
	const [selectedPages, setSelectedPages] = useState([]);
	const [pagesCount, setPagesCount] = useState(0);

	useEffect(() => {
		fetchReport();
	}, []);

	useEffect(() => {
		onSelectAllPages();
	}, [pagesCount]);

	const fetchReport = () => {
		setLoaderDisplay(true);
		let fetchRequest;
		if (activeView === 'GRI' || activeView === 'UAE_FDL') {
			fetchRequest = ReportService.getReport(reportId);
		} else if (activeView === 'GHG') {
			fetchRequest = ReportService.getGHGReport(reportId);
		} else {
			fetchRequest = ReportService.getESRSReport(reportId);
		}
		fetchRequest.then(res => {
			setLoaderDisplay(false);
			if (res.status !== 200) return;

			const reportData = res.data;
			let count = 0;
			reportData?.sections?.forEach((section, index) => {
				section.pages?.sort((a, b) => {
					return a.sortOrder - b.sortOrder;
				});
				count += section.pages?.length;
			});
			reportData?.sections?.sort((a, b) => {
				return a.sortOrder - b.sortOrder;
			});
			reportData?.sections?.forEach((section, index) => {
				section.index = index;
				section.pages?.forEach((page, pIndex) => {
					page.index = pIndex;
				});
			});
			if (activeView === 'GRI') {
				ReportingUtils.normalizeGriContentsSectionName(reportData, t('Contents.label'));
				ReportingUtils.applyGriTableOfContentsToReport(reportData, t('TableofContents.label'));
			}
			setPagesCount(count);
			setReport(reportData);
		}).catch(error => {
			setLoaderDisplay(false);
			toast.error(error.title, {
				position: toast.POSITION.TOP_RIGHT,
			});
		});
	};

	const exportReport = () => {
		if (exportAs === ExportAs.WORD) {
			exportAsWord();
		}
	};

	const exportAsWord = () => {
		if (selectedPages.length > 0) {
			if (activeView === 'GRI') {
				setLoaderDisplay(true);
				const htmlContent = ReportWordExportUtils.buildGriWordDocument(report, selectedPages, t);
				Utils.replaceImgWithBase64(
					htmlContent,
					(result) => {
						const convertedDoc = htmlDocx.asBlob(result);
						const fileURL = window.URL.createObjectURL(convertedDoc);
						const link = document.createElement('a');
						link.href = fileURL;
						link.download = `${report.name}.docx`;
						link.click();
						onHide();
						setLoaderDisplay(false);
					},
				);
				return;
			}

			const skipBackCoverInWord =
				activeView === 'ESRS' || activeView === 'VSME';
			const sectionIsRoot = (section) =>
				section.type === ReportSection.ROOT || section.type === 'ROOT';

			let wordExportPageTotal = 0;
			report.sections?.forEach((section) => {
				section.pages?.forEach((page) => {
					if (selectedPages?.indexOf(page.id) < 0) return;
					if (skipBackCoverInWord && sectionIsRoot(section)) return;
					wordExportPageTotal += 1;
				});
			});

			if (wordExportPageTotal === 0) {
				setLoaderDisplay(false);
				toast.warning(t('Please select any pages to export'), {
					position: toast.POSITION.TOP_RIGHT,
				});
				return;
			}

			setLoaderDisplay(true);
			const ghgWordTableSpacing =
				isGhgLikeExportView(activeView)
					? `${WORD_DOCX_GHG_COMPAT_INLINE_CSS}${WORD_DOCX_GHG_TABLE_P_FULL_WIDTH_CSS}${WORD_DOCX_GHG_INNER_IMAGE_CSS}${WORD_DOCX_GHG_COVER_NO_BORDER_CSS}${WORD_DOCX_GHG_COVER_BRSR_TABLE_BORDER_RESET_CSS}`
					: '';
			let htmlContent = `<html><head><meta charset="UTF-8"></head><style>.word-cover-page{position:relative;min-height:100%;padding:1in;background-color:#f8fafc;}.word-cover-page .word-cover-content{position:relative;z-index:1;}.word-cover-page .cover_imgBox img{width:250px;object-fit:contain;}.word-cover-page .campany_logobox img{width:120px;object-fit:contain;}.word-cover-page img{width:250px;object-fit:contain;}${ghgWordTableSpacing}</style><body>`;
			if (activeView === 'ESRS' || activeView === 'VSME' || isGhgLikeExportView(activeView)) {
				htmlContent = `<html><head><meta charset="UTF-8"></head><style>@page {size: A4 landscape;}body {font-family: Arial, sans-serif;margin: 0.5in;}.word-cover-page{position:relative;min-height:100%;padding:1in;background-color:#f8fafc;}.word-cover-page .word-cover-content{position:relative;z-index:1;}.word-cover-page .cover_imgBox img{width:250px;object-fit:contain;}.word-cover-page .campany_logobox img{width:120px;object-fit:contain;}.word-cover-page img{width:250px;object-fit:contain;}${ghgWordTableSpacing}</style><body>`
			} else if (activeView === 'DOUBLE_MAT') {
				htmlContent = `<html><head><meta charset="UTF-8"></head><style>@page {size: A4 landscape;}body {font-family: Arial, sans-serif;margin: 0.25in;}br{display: block;margin:0;line-height:1;}.word-cover-page{position:relative;min-height:100%;padding:1in;background-color:#f8fafc;}.word-cover-page .word-cover-content{position:relative;z-index:1;}.word-cover-page .cover_imgBox img{width:250px;object-fit:contain;}.word-cover-page .campany_logobox img{width:120px;object-fit:contain;}.word-cover-page img{width:250px;object-fit:contain;}</style><body>`
			}
			const contentPageHTML = buildExportModalTocHtml(report.sections, t('TableofContents.label'));
			let count = 0;
			report.sections?.forEach(section => {
				section.pages?.forEach((page) => {
					if (selectedPages?.indexOf(page.id) < 0) return;
					if (skipBackCoverInWord && sectionIsRoot(section)) return;
					count += 1;
					let decodedHTML = page.htmlContent ? decodeURIComponent(escape(window.atob(page.htmlContent))) : null;
					const isGhgContentsPage =
						activeView === 'GHG'
						&& (page.type === ReportPageType.CONTENTS || page.type === 'CONTENTS');
					if (isGhgContentsPage) {
						decodedHTML = prepareGhgTocPageHtmlForWord(
							buildGhgTableOfContentsHtml((key) => escapeTocHtml(t(key))),
						);
					} else if (decodedHTML !== null) {
						// Replace <br> tags with <p> tags for better formatting
						decodedHTML = decodedHTML.replace(/<br\s*\/?>/g, '<p style="margin:0; line-height:1;"></p>');
						const isCoverPage = page.type === ReportPageType.TITLE || page.type === 'TITLE' || page.type === 0;
						// Apply width and height to cover page images for Word export
						if (isCoverPage && activeView !== 'GHG') {
							decodedHTML = decodedHTML.replace(/<img([^>]*)>/gi, (match) => {
								const hasWidth = /width\s*=/i.test(match);
								if (hasWidth) return match;
								return match.replace(/<img/, '<img width="250"');
							});
							if (activeView === 'GHG') {
								decodedHTML = stripGhgCoverPageBorderForWord(
									prepareGhgModalCoverHtmlForWord(decodedHTML),
								);
							}
						}
					}
					const isContentsPage =
						page.type === ReportPageType.CONTENTS || page.type === 'CONTENTS';
					if (isContentsPage && activeView != 'ESRS' && activeView != 'VSME' && !isGhgLikeExportView(activeView)) {
						const tocHtml = decodedHTML || contentPageHTML;
						const tocBlock = count === wordExportPageTotal
							? `<div>${tocHtml}</div>`
							: `<div>${tocHtml}<br clear="all" style="page-break-before:always"/></div>`;
						htmlContent += tocBlock;
					} else {
						const isCoverPage = page.type === ReportPageType.TITLE || page.type === 'TITLE' || page.type === 0;
						const pageDiv = isCoverPage
							? (activeView === 'GHG'
								? `<div class="ghg-word-export-page">${decodedHTML}</div>`
								: `<div class="word-cover-page"><div class="word-cover-content">${decodedHTML}</div></div>`)
							: decodedHTML;
						if (count === wordExportPageTotal) {
							htmlContent += `<div>${pageDiv}</div>`;
						} else {
							htmlContent += `<div>${pageDiv}<br clear="all" style="page-break-before:always"/></div>`;
						}
					}
				});
			});
			htmlContent += `</body></html>`;
			if (activeView === 'GHG') {
				htmlContent = prepareGhgWordExportDocument(htmlContent);
			}
			Utils.replaceImgWithBase64(
				htmlContent,
				(result) => {
					const convertedDoc = htmlDocx.asBlob(result);
					const fileURL = window.URL.createObjectURL(convertedDoc);
					const link = document.createElement('a');
					link.href = fileURL;
					link.download = `${report.name}.docx`;
					link.click();
					onHide();
					setLoaderDisplay(false);
				},
				{ ghgInnerImages: activeView === 'GHG' },
			);
		} else {
			setLoaderDisplay(false);
			toast.warning('Please select any pages to export', {
				position: toast.POSITION.TOP_RIGHT,
			});
		}
	};

	const onPageSelection = (pageId) => {
		const p = [...selectedPages];
		const index = selectedPages.indexOf(pageId);
		if (index >= 0) {
			p.splice(index, 1);
		} else {
			p.push(pageId);
		}
		setSelectedPages(p);
	};

	const onSelectAllPages = () => {
		if (pagesCount === selectedPages.length) {
			setSelectedPages([]);
			return;
		}
		const p = [];
		report?.sections?.forEach(section => {
			section.pages?.forEach(page => {
				p.push(page.id);
			});
		});
		setSelectedPages(p);
	};

	return (
		<>
			<Modal show={true} onHide={() => onHide()}
				aria-labelledby="contained-modal-title-vcenter"
				size="lg"

				centered>
				<Modal.Header closeButton className="border-0">
					< Modal.Title as="h5">
						{exportAs === ExportAs.PDF ? t('DownloadAsPDF.label') : t('DownloadAsWord.label')}  "{report.name}"
					</Modal.Title>
				</Modal.Header>

				<Modal.Body className='p-0'>
					<div className="DownloadWord_modalBody">

						<div className="DownloadWord_modalBody_in">

							<Form noValidate>
								<div className='pb-2'>
									<input
										type="checkbox"
										className="form-check-input cursor"
										id={`selectAllpageCheckbox`}
										checked={selectedPages?.length === pagesCount}
										onChange={() => onSelectAllPages()}
									/>
									<label className="form-check-label  cursor ps-2" htmlFor={`selectAllpageCheckbox`}>
										{t('SelectAll.label')}

									</label>
								</div>

								{
									report?.sections?.map((section, sIndex) => {
										return (
											<div className='DownloadWord_section' key={`section-${sIndex}`}>

												<h6>{section.name}</h6>

												<ul>
													{
														section?.pages?.map((page, pIndex) => {
															return (
																<li className="pt-1" key={`page-${sIndex}-${pIndex}`}>
																	<input
																		type="checkbox"
																		className="form-check-input cursor"
																		id={`pageCheckbox-${sIndex}-${pIndex}`}
																		checked={selectedPages.indexOf(page.id) >= 0}
																		onChange={() => onPageSelection(page.id)}
																	/>
																	<label className="form-check-label cursor px-1" htmlFor={`pageCheckbox-${sIndex}-${pIndex}`}>
																		{page.name}
																	</label>
																</li>
															)
														})
													}
												</ul>
											</div>

										);
									})
								}
							</Form>
						</div>
					</div>

					<div className="text-center p-3">
						<Button variant="secondary" className="mx-2" onClick={() => onHide()}>{t('Cancel.label')}</Button>
						<Button variant="primary" onClick={() => exportReport()}>{t('Download.label')}</Button>
					</div>
				</Modal.Body>

			</Modal>
		</>
	);
}

export default withTranslation()(ReportExportModal);

